import type { Chart, ChartDataset, TooltipItem } from 'chart.js';
import type { UnionToIntersection } from 'chart.js/dist/types/utils';
import { useCallback, useMemo } from 'react';

import {
  CHART_FIELD,
  CHART_GRAPH,
  CHART_TOOLTIP_TARGET_TYPE,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import type { DashboardCardInfoT } from '@/features/Dashboard/types';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';

import { useOpenChartClickDialog } from './use-open-chart-click-dialog';

const useEventPlugin = ({
  meta,
  hideTooltip,
  dashboardCardInfo,
}: {
  meta: ChartBase & { name: string };
  hideTooltip?: () => void;
  dashboardCardInfo?: DashboardCardInfoT;
}) => {
  const openChartClickDialog = useOpenChartClickDialog();
  const openChartRawDetailModal = useCallback(
    (meta: ChartBase & { name: string }, chart: Chart) => {
      const modalOptions: ChartFeedbackListModalOptions<CellFeedbackListModalOptions> =
        {
          chart: meta,
          isAll: false,
          cell: {
            compared_target: false,
            series_index: 0,
            item_x: '',
            item_x_name: '',
          },
          dashboardCardInfo,
        };

      if (meta.type === CHART_TYPE.SINGLE_AXIS) {
        switch (meta.graph) {
          case CHART_GRAPH.PIE:
          case CHART_GRAPH.BAR: {
            const dataPoint = chart.tooltip
              ?.dataPoints?.[0] as TooltipItem<'bar'>;
            const dataIndex = dataPoint?.dataIndex || 0;

            // custom field type 확장 불가로 인한 type assertion
            const dataset = dataPoint?.dataset as UnionToIntersection<
              ChartDataset<'bar'>
            > & {
              meta: StringNumberValue[];
            };

            if (!dataset) {
              console.error('dataset is undefined');
              return;
            }

            const alias = dataset.meta[dataIndex];
            const isCompareTarget =
              chart.tooltip?.dataPoints[0].dataset.label ===
              CHART_TOOLTIP_TARGET_TYPE.COMPARE;

            if (meta.field.type !== CHART_FIELD.FEEDBACK_ALL) {
              modalOptions.cell.item_x = alias;
              modalOptions.cell.item_x_name = dataPoint.label;
            }

            modalOptions.cell.compared_target = isCompareTarget;
            break;
          }
          case CHART_GRAPH.BAR_STACKED: {
            const tooltip = chart.tooltip;
            const dataPoints = tooltip?.dataPoints[0];
            const dataIndex = dataPoints?.dataIndex || 0;

            // custom field type 확장 불가로 인한 type assertion
            const dataset = dataPoints?.dataset as UnionToIntersection<
              ChartDataset<'bar'>
            > & {
              meta: {
                x: StringNumberValue;
                y: string[];
              };
            };

            const alias = dataset.meta.x;
            const dateLabel = dataset.meta.y[dataIndex];

            const isCompareTarget =
              chart.tooltip?.dataPoints[0].dataset.stack ===
              CHART_TOOLTIP_TARGET_TYPE.COMPARE;

            const { start } = chartUtils.parseDateFormatByFrequency(
              dateLabel,
              chartUtils.getFrequencyByTermUnit(meta.target.term.unit),
            );

            if (meta.field.type !== CHART_FIELD.FEEDBACK_ALL) {
              modalOptions.cell.item_x = alias;
              modalOptions.cell.item_x_name = dataset?.label || '';
            }
            // start는 parseDateFormatByFrequency에서 .toJSON()으로 반환된 UTC ISO 문자열
            modalOptions.cell.item_y = start;
            modalOptions.cell.item_y_name = dataPoints?.label || '';
            modalOptions.cell.compared_target = isCompareTarget;

            break;
          }
          case CHART_GRAPH.COMBO_CHART: {
            const dataPoint = chart.tooltip
              ?.dataPoints?.[0] as TooltipItem<'bar'>;
            const dataIndex = dataPoint?.dataIndex || 0;

            // custom field type 확장 불가로 인한 type assertion
            const dataset = dataPoint?.dataset as UnionToIntersection<
              ChartDataset<'bar'>
            > & {
              meta: {
                x: StringNumberValue[];
                y: StringNumberValue[];
              };
            };

            if (!dataset) {
              console.error('dataset is undefined');
              return;
            }

            const alias = dataset.meta.x[dataIndex];

            if (meta.field.type !== CHART_FIELD.FEEDBACK_ALL) {
              modalOptions.cell.item_x = alias;
              modalOptions.cell.item_x_name = dataPoint.label;
            }

            break;
          }
          case CHART_GRAPH.LINE:
          case CHART_GRAPH.AREA_STACKED:
            // Custom Chart tooltip에서 처리
            return;
          default:
            throw new Error('Invalid graph type');
        }
      }
      if (meta.type === CHART_TYPE.CROSS_AXIS) {
        switch (meta.graph) {
          case CHART_GRAPH.BAR_STACKED: {
            const tooltip = chart.tooltip;
            const dataPoints = tooltip?.dataPoints[0];
            const dataIndex = dataPoints?.dataIndex || 0;

            // custom field type 확장 불가로 인한 type assertion
            const dataset = dataPoints?.dataset as UnionToIntersection<
              ChartDataset<'bar'>
            > & {
              meta: {
                x: string[];
                y: StringNumberValue;
              };
            };

            const isCompareTarget =
              chart.tooltip?.dataPoints[0].dataset.stack ===
              CHART_TOOLTIP_TARGET_TYPE.COMPARE;

            modalOptions.cell.item_x = dataset.meta.x[dataIndex];
            modalOptions.cell.item_x_name = dataPoints?.label || '';
            modalOptions.cell.item_y = dataset.meta.y;
            modalOptions.cell.item_y_name = dataset.label || '';
            modalOptions.cell.compared_target = isCompareTarget;
            break;
          }
          default:
            throw new Error('Invalid graph type');
        }
      }

      openChartClickDialog({
        alias: JSON.stringify(meta),
        type: DETAIL_MODAL_TYPE.CHART,
        idx: 0,
        keys: [
          modalOptions.cell.item_x,
          modalOptions.cell.item_y,
          modalOptions.cell.compared_target,
        ],
        options: modalOptions,
      });
    },
    [dashboardCardInfo, openChartClickDialog],
  );

  const eventPlugin = useMemo(() => {
    return {
      id: 'eventPlugin',
      afterEvent(chart: Chart, args: any) {
        if (args.event.type === 'click') {
          openChartRawDetailModal(meta, chart);
        }
        if (args.event.type === 'mouseleave') {
          hideTooltip?.();
        }
      },
    };
  }, [hideTooltip, meta, openChartRawDetailModal]);

  return eventPlugin;
};

export default useEventPlugin;
